/**
 * Helpers that translate Section 1's scope-row representation into the
 * Product-centric view used by Section 2 (Commercial) and Section 3 (AMC).
 *
 * Section 1 stores selections as scope rows where each row is either:
 *   - a Module of a Product (productCode set, moduleCode set)
 *   - a standalone Product without modules (productCode set, moduleCode null)
 *   - a Custom row (source="custom", productCode null)
 *
 * Sections 2 & 3 work at the PRODUCT level. This module collapses scope
 * rows back into Products while preserving operator-entered module prices.
 *
 * Pure frontend; no backend coupling.
 */

import { PRODUCT_MASTER } from "./productMasterHierarchy";

/**
 * Stable key for a Product across the lifetime of a prototype. Survives
 * reloads (uses scope-row identifiers that don't change after creation).
 */
export function productKeyForScopeRow(row) {
    if (!row) return null;
    if (row.source === "custom") return `custom:${row.id}`;
    if (row.productCode) return `master:${row.productCode}`;
    return null;
}

/**
 * Look up a Product Master entry by its `code` (the `productCode` carried
 * on every Master scope row). Returns the `product` node (with `.modules`
 * and `.description`) or `null`.
 */
export function findMasterProduct(productCode) {
    if (!productCode) return null;
    for (const top of PRODUCT_MASTER) {
        if (top.type === "category") {
            for (const p of (top.products || [])) {
                if (p.code === productCode) return p;
            }
        } else if (top.type === "product" && top.code === productCode) {
            return top;
        }
    }
    return null;
}

/**
 * Collapse a list of scope rows into an ordered list of Products. Each
 * product entry carries:
 *   - key            stable product key (see productKeyForScopeRow)
 *   - label          display name (e.g. "aiM-Wealth", "Mobile App Cust.")
 *   - description    Master-side description (or "" for custom rows)
 *   - categoryLabel  parent Business Category if any (else null)
 *   - source         "master" | "custom"
 *   - hasModules     true if at least one module-row is present
 *   - scopeRows      every scope row that belongs to this Product, in
 *                    the order they appear in `scope`.
 *
 * Order: products appear in the order they were first encountered in
 * `scope`. Custom products always trail Master products as a single
 * contiguous block.
 */
export function aggregateScopeByProduct(scope) {
    const byKey = new Map();
    const masterOrder = [];
    const customOrder = [];

    (scope || []).forEach((row) => {
        const key = productKeyForScopeRow(row);
        if (!key) return;
        if (!byKey.has(key)) {
            const isCustom = row.source === "custom";
            const masterProduct = !isCustom ? findMasterProduct(row.productCode) : null;
            const entry = {
                key,
                label: isCustom ? row.productName : (row.groupLabel || row.productName || row.productCode),
                // Owner directive 2026-09-29 — custom products carry an
                // operator-written "About this product" note; Master rows
                // keep the catalogue description.
                description: isCustom ? (row.description || "") : (masterProduct?.description || ""),
                noDeliveryModes: !!masterProduct?.noDeliveryModes,
                categoryLabel: row.categoryLabel || null,
                source: isCustom ? "custom" : "master",
                hasModules: false,
                scopeRows: [],
            };
            byKey.set(key, entry);
            (isCustom ? customOrder : masterOrder).push(key);
        }
        const entry = byKey.get(key);
        entry.scopeRows.push(row);
        if (row.moduleCode) entry.hasModules = true;
    });

    return [...masterOrder, ...customOrder].map((k) => byKey.get(k));
}
